import { useState } from "react";
import { toast } from "sonner";
import { ArrowsClockwise, CheckCircle, Warning, Sparkle, Quotes } from "@phosphor-icons/react";
import { api, fmtErr } from "../../lib/api";

const BASE = process.env.REACT_APP_BACKEND_URL;

const CHIP = {
  spec: "text-[var(--c-green)] border-[var(--c-green)]/40 bg-[var(--c-green)]/10",
  user: "text-[var(--c-text)] border-[var(--c-border)] bg-[var(--c-surface-2)]",
  rule: "text-[var(--c-accent)] border-[var(--c-accent)]/40 bg-[var(--c-accent)]/10",
  assumed: "text-[var(--c-amber)] border-[var(--c-amber)]/40 bg-[var(--c-amber)]/10",
  conflict: "text-[var(--c-red)] border-[var(--c-red)]/50 bg-[var(--c-red)]/10",
  missing: "text-[var(--c-red)] border-[var(--c-red)]/50 bg-[var(--c-red)]/10",
};

/** Phase 2 confirmation UI: every input, where it came from, and the quoted sentence.
 *  Generation is blocked until nothing required is missing and no conflict is unresolved. */
export default function InputsPanel({ projectId, data, onChange, onGenerated, busy, setBusy }) {
  const [edit, setEdit] = useState({});
  const [job, setJob] = useState(null);

  if (!data) return <div className="p-4 text-xs text-[var(--c-muted)]" data-testid="inputs-loading">Loading inputs…</div>;

  const save = async (fid, value, note) => {
    try {
      const { data: d } = await api.post(`/engine/${projectId}/inputs/override`, { field_id: fid, value, note });
      onChange(d);
      setEdit((e) => ({ ...e, [fid]: undefined }));
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const extract = async () => {
    try {
      setBusy(true);
      const { data: d } = await api.post(`/engine/${projectId}/extract`);
      setJob({ status: "running" });
      const tick = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/engine/${projectId}/extract/${d.job_id}`);
          setJob(j);
          if (j.status !== "running") {
            clearInterval(tick);
            setBusy(false);
            if (j.status === "failed") toast.error(j.error || "Extraction failed");
            else {
              toast.success(`Extraction complete — ${j.found} parameters found`);
              const { data: fresh } = await api.get(`/engine/${projectId}/inputs`);
              onChange(fresh);
            }
          }
        } catch { clearInterval(tick); setBusy(false); }
      }, 2500);
    } catch (e) { setBusy(false); toast.error(fmtErr(e)); }
  };

  const generate = async () => {
    try {
      setBusy(true);
      await api.post(`/engine/${projectId}/inputs/confirm`);
      const { data: model } = await api.post(`/engine/${projectId}/generate`);
      toast.success(`Design model generated — ${model.parts.length} parts, ${model.rules_applied.length} rules`);
      onGenerated(model);
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  const openSource = (row) => {
    if (!row.file_id) return;
    const q = row.quote ? `&quote=${encodeURIComponent(row.quote)}` : "";
    window.open(`${BASE}/api/files/${row.file_id}/highlight?page=${row.page || 1}${q}#page=${row.page || 1}`, "_blank");
  };

  return (
    <div className="flex flex-col gap-3" data-testid="engine-inputs-panel">
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] px-3 py-2">
        <span className="font-mono text-[10px] text-[var(--c-muted)]">formigen.inputs/1.0</span>
        {data.ready ? (
          <span className="flex items-center gap-1 font-mono text-[10px] text-[var(--c-green)]" data-testid="inputs-ready">
            <CheckCircle size={12} /> inputs complete
          </span>
        ) : (
          <span className="flex items-center gap-1 font-mono text-[10px] text-[var(--c-red)]" data-testid="inputs-blocked">
            <Warning size={12} /> {data.missing.length} missing · {data.conflicts.length} conflict(s)
          </span>
        )}
        {!!data.assumed.length && (
          <span className="font-mono text-[10px] text-[var(--c-amber)]" data-testid="inputs-assumed">
            {data.assumed.length} assumed
          </span>
        )}
        <button data-testid="inputs-extract-btn" disabled={busy} onClick={extract}
          className="ml-auto flex h-7 items-center gap-1.5 rounded-sm border border-[var(--c-border)] px-2.5 text-[11px] text-[var(--c-text)] hover:border-[var(--c-accent)] disabled:opacity-40">
          <ArrowsClockwise size={12} /> Re-read the documents
        </button>
        <button data-testid="inputs-generate-btn" disabled={busy || !data.ready} onClick={generate}
          title={data.ready ? "" : "Resolve the missing inputs and conflicts first"}
          className="flex h-7 items-center gap-1.5 rounded-sm border border-[var(--c-accent)]/60 bg-[var(--c-accent)]/15 px-2.5 text-[11px] text-[var(--c-accent)] hover:bg-[var(--c-accent)]/25 disabled:opacity-40">
          <Sparkle size={12} /> Confirm &amp; generate
        </button>
      </div>

      {job?.status === "running" && (
        <div className="rounded-sm border border-[var(--c-accent)]/40 bg-[var(--c-accent)]/10 px-3 py-2 font-mono text-[10px] text-[var(--c-accent)]"
          data-testid="inputs-extract-progress">
          reading {job.file || "documents"} — chunk {job.done}/{job.total || "?"}
        </div>
      )}

      {!!data.missing.length && (
        <div className="rounded-lg border border-[var(--c-red)]/40 bg-[var(--c-red)]/10 p-3" data-testid="inputs-missing">
          <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-[var(--c-red)]">
            Stop and ask — nothing is assumed silently
          </div>
          <ul className="m-0 list-disc pl-4 text-[12px] text-[var(--c-text)]">
            {data.missing.map((m) => <li key={m.id}>{m.ask}</li>)}
          </ul>
        </div>
      )}

      {data.groups.map((g) => {
        const rows = data.fields.filter((f) => f.group === g.id);
        if (!rows.length) return null;
        return (
          <div key={g.id} className="overflow-hidden rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)]">
            <div className="border-b border-[var(--c-border)] px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-[var(--c-muted)]">
              {g.title}
            </div>
            <table className="w-full text-[12px]">
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`input-row-${r.id}`}
                    className={`border-b border-[var(--c-border)]/50 ${r.origin === "conflict" ? "bg-[var(--c-red)]/5" : ""}`}>
                    <td className="px-3 py-1.5 text-[var(--c-text)]">
                      {r.label}{r.required ? <span className="text-[var(--c-red)]"> *</span> : null}
                      {r.quote && (
                        <button data-testid={`input-quote-${r.id}`} onClick={() => openSource(r)}
                          title={r.quote}
                          className="ml-1.5 inline-flex items-center gap-1 align-middle font-mono text-[9px] text-[var(--c-accent)] hover:underline">
                          <Quotes size={9} />{r.clause || (r.page ? `p.${r.page}` : "source")}
                        </button>
                      )}
                      {r.origin === "conflict" && (
                        <div className="mt-1 flex flex-wrap items-center gap-1.5 font-mono text-[10px]">
                          <span className="text-[var(--c-muted)]">document</span>
                          <button data-testid={`input-pick-engine-${r.id}`} onClick={() => save(r.id, r.engine_value, "picked the document value")}
                            className="rounded-sm border border-[var(--c-green)]/50 px-1.5 text-[var(--c-green)]">{String(r.engine_value)}</button>
                          <span className="text-[var(--c-muted)]">vs intake</span>
                          <button data-testid={`input-pick-intake-${r.id}`} onClick={() => save(r.id, r.intake_value, "picked the intake value")}
                            className="rounded-sm border border-[var(--c-amber)]/50 px-1.5 text-[var(--c-amber)]">{String(r.intake_value)}</button>
                        </div>
                      )}
                      {r.note && r.origin !== "conflict" && (
                        <div className="font-mono text-[9px] text-[var(--c-muted)]">{r.note}</div>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono text-[11px] tabular-nums text-[var(--c-text)]">
                      {edit[r.id] !== undefined ? (
                        <span className="flex items-center justify-end gap-1">
                          <input autoFocus value={edit[r.id]} data-testid={`input-edit-${r.id}`}
                            onChange={(e) => setEdit((s) => ({ ...s, [r.id]: e.target.value }))}
                            onKeyDown={(e) => e.key === "Enter" && save(r.id, r.type === "number" ? Number(edit[r.id]) : edit[r.id])}
                            className="h-6 w-24 rounded-sm border border-[var(--c-accent)] bg-[var(--c-bg)] px-1.5 text-right text-[11px] outline-none" />
                          <button data-testid={`input-save-${r.id}`}
                            onClick={() => save(r.id, r.type === "number" ? Number(edit[r.id]) : edit[r.id])}
                            className="text-[var(--c-accent)]">save</button>
                        </span>
                      ) : (
                        <button data-testid={`input-value-${r.id}`}
                          onClick={() => setEdit((s) => ({ ...s, [r.id]: r.value ?? "" }))}
                          className="hover:underline">
                          {r.value === null || r.value === undefined ? "—" : String(r.value)}
                          {r.unit ? ` ${r.unit}` : ""}
                        </button>
                      )}
                    </td>
                    <td className="w-20 px-2 py-1.5 text-right">
                      <span className={`rounded-sm border px-1.5 py-0.5 font-mono text-[9px] ${CHIP[r.origin] || CHIP.rule}`}>
                        {r.origin}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}
